import React, { useRef, useEffect, useState } from 'react';
import { Send, Sparkles, Layout, Loader2, ChevronRight, ChevronLeft, PanelRightClose, PanelRightOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EnhancedPaperCard from './EnhancedPaperCard'; 
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// --- Configuration: Query Categories ---
const QUERY_CATEGORIES = {
    "Single Paper Exploration": [
        "summarize the parameter impact",
        "extract the experiment settings",
        "extract the experiment setup",
        "extract the experiment baselines",
        "extract the experiment datasets",
        "extract the experiment metrics",
        "extract the ablation studies",
        "extract the parameter studies",
        "extract the experiment results",
        "extract the proposed methods",
        "extract the problem definition",
        "extract the problem input",
        "extract the problem output",
        "extract the problem goal",
        "rank the datasets",
        "rank the variants",
        "check inconsistencies on metrics",
    ],
    "Set of Papers Exploration": [
        "rank performance cross papers",
        "summarize common settings cross papers",
        "summarize methods cross papers",
        "summarize missing experiment settings cross papers",
        "compare problem definitions cross papers",
        "check inconsistencies on experiment results cross papers",
        "search papers with metadata constraints:",
        "search papers with detailed information:"
    ],
    "Topic Level Exploration": [
        "propose novel research ideas",
        "research trend analysis",
        "visualization of research trend analysis (chart)",
        "select 2 milestone(representative) papers between year 2018 and year 2025 (you can change your k, y1, y2 values)"
    ]
};

const CHART_COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a05195', '#d45087'
];

const transformChartData = (rawJson) => {
    try {
        const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
        const { x, y, tags, data } = parsed;
        if (!data || !tags) return null;

        const xDataKey = x.toLowerCase(); 
        const yDataKey = y.toLowerCase(); 

        const groupedData = {};

        data.forEach(item => {
            const xVal = item[xDataKey];
            if (!groupedData[xVal]) {
                groupedData[xVal] = { [xDataKey]: xVal };
            }
            groupedData[xVal][item.node_id] = item[yDataKey];
        });

        const pivotedData = Object.values(groupedData);

        pivotedData.sort((a, b) => {
            const valA = a[xDataKey];
            const valB = b[xDataKey];
            if (!isNaN(valA) && !isNaN(valB)) {
                return Number(valA) - Number(valB);
            }
            return String(valA).localeCompare(String(valB));
        });

        return { 
            data: pivotedData, 
            xKey: xDataKey, 
            tags: tags 
        };
    } catch (e) {
        console.error("Failed to transform chart data:", e);
        return null;
    }
};

function CenterPanel({
    viewMode, 
    messages,
    papersInView, 
    loading,
    chatInput,
    setChatInput,
    handleSendMessage,
    handlePaperSearch, 
    onAddToKB, 
    currentKB,
    isSearchActive,
    isRightPanelOpen,
    onToggleRightPanel
}) {
    const chatEndRef = useRef(null);
    const [showPredefMenu, setShowPredefMenu] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null); 

    // Auto-scroll to bottom of chat
    useEffect(() => {
        if (viewMode === 'chat' && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, viewMode]);

    const handlePredefinedQuery = (query) => {
        setChatInput(query);
        setShowPredefMenu(false);
        setActiveCategory(null);
    };

    const toggleMenu = () => {
        if (showPredefMenu) {
            setShowPredefMenu(false);
            setTimeout(() => setActiveCategory(null), 200); 
        } else {
            setShowPredefMenu(true);
        }
    };

    const handleInputKeypress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); 
            if (!loading.action && !loading.papers) { 
                if (viewMode === 'paperSearchResults') {
                    handlePaperSearch(); 
                } else {
                    handleSendMessage();
                }
            }
        }
    };

    const markdownComponents = {
        table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '14px', border: '1px solid var(--border-color)' }} {...props} />
            </div>
        ),
        thead: ({ node, ...props }) => <thead style={{ backgroundColor: 'var(--primary-color)', color: 'white' }} {...props} />,
        th: ({ node, ...props }) => <th style={{ border: '1px solid var(--border-color)', borderBottom: '2px solid var(--primary-dark)', padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'white' }} {...props} />,
        td: ({ node, ...props }) => <td style={{ border: '1px solid var(--border-color)', padding: '8px 12px', verticalAlign: 'top' }} {...props} />,
        tr: ({ node, ...props }) => <tr style={{ backgroundColor: 'transparent', borderBottom: '1px solid var(--border-color)' }} {...props} />
    };

    const isProcessing = loading.action || loading.papers;

    return (
        <main className="static-main" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            
            {/* Toggle Right Panel Button */}
            <div style={{ 
                position: 'absolute', 
                top: '16px', 
                right: '16px', 
                zIndex: 10
            }}>
                <button
                    onClick={onToggleRightPanel}
                    style={{
                        padding: '8px',
                        background: 'white',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'var(--shadow-sm)',
                        transition: 'all 0.2s'
                    }}
                    title={isRightPanelOpen ? "Close Right Panel" : "Open Right Panel"}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                    {isRightPanelOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
                </button>
            </div>

            {/* 1. Content Area (Scrollable) */}
            <div className="content-area" style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '0 20px 8px', 
                background: 'var(--bg-primary)',
                width: '100%'
            }}>
                {viewMode === 'chat' && (
                    messages.length === 0 ? (
                        <div className="content-placeholder">
                            {/* <Layout size={64} style={{ opacity: 0.1, margin: '0 auto 16px' }} /> */}
                            {/* <div>Workspace Canvas</div>
                            <div style={{ fontSize: '13px', opacity: 0.7 }}>(Chat messages & answers appear here)</div> */}
                        </div>
                    ) : (
                        <div className="space-y-4 max-w-3xl mx-auto w-full">
                            {messages.map((msg) => (
                                <div key={msg.id} style={{
                                    display: 'flex',
                                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                                }}>
                                    <div style={{
                                        maxWidth: '85%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        backgroundColor: msg.sender === 'user' ? 'var(--primary-color)' : 'var(--bg-secondary)',
                                        color: msg.sender === 'user' ? 'white' : 'var(--text-primary)',
                                        boxShadow: 'var(--shadow-sm)',
                                        border: msg.sender !== 'user' ? '1px solid var(--border-color)' : 'none'
                                    }}>
                                        {msg.msg_type === 'chart' ? (
                                            (() => {
                                                const chartData = transformChartData(msg.text);
                                                if (!chartData) return <div style={{color: 'red'}}>Error loading chart data</div>;

                                                return (
                                                    <div style={{ width: '100%', minWidth: '500px', height: 350, backgroundColor: 'white', borderRadius: '8px', padding: '10px' }}>
                                                        <h4 style={{marginBottom: '10px', color: '#333', fontSize: '14px', fontWeight: '600'}}>
                                                            Research Trend Analysis (Paper Count over Years)
                                                        </h4>
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <LineChart
                                                                data={chartData.data}
                                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                                            >
                                                                <CartesianGrid strokeDasharray="3 3" />
                                                                <XAxis dataKey={chartData.xKey} />
                                                                <YAxis />
                                                                <Tooltip />
                                                                <Legend />
                                                                {chartData.tags.map((tag, index) => (
                                                                    <Line 
                                                                        key={tag}
                                                                        type="monotone" 
                                                                        dataKey={tag} 
                                                                        name={tag}
                                                                        stroke={CHART_COLORS[index % CHART_COLORS.length]} 
                                                                        activeDot={{ r: 8 }} 
                                                                        connectNulls
                                                                    />
                                                                ))}
                                                            </LineChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div className="prose" style={{ fontSize: '14px' }}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                                    {msg.text}
                                                </ReactMarkdown>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>
                    )
                )}

                {viewMode === 'paperSearchResults' && (
                    <div className="max-w-4xl mx-auto w-full">
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--text-primary)' }}>Search Results</h2>
                        {loading.papers ? (
                            <div className="flex justify-center p-8"><span className="animate-spin">⌛</span> Searching...</div>
                        ) : papersInView.length === 0 ? (
                            <div className="text-center p-8 text-gray-500">No papers found. Try the query bar below.</div>
                        ) : (
                            papersInView.map((paper, idx) => (
                                <EnhancedPaperCard key={paper.id || idx} paper={paper} onAddToKB={onAddToKB} />
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* 2. Bottom Query Bar (Fixed) */}
            <div className="query-bar-wrapper">
                <div className="query-bar">
                    <input 
                        className="query-input" 
                        placeholder={viewMode === 'paperSearchResults' ? "Enter keywords to find papers..." : "Ask anything or Select a suggested query."}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={handleInputKeypress}
                        disabled={isProcessing} 
                    />
                    
                    <div className="predef-picker">
                        <button 
                            className="predef-btn"
                            onClick={toggleMenu}
                            disabled={isProcessing}
                        >
                            <span>Suggested</span>
                            <Sparkles size={14} />
                        </button>
                        
                        {/* Categorized Dropdown Menu */}
                        {showPredefMenu && (
                            <div className="predef-menu open">
                                {!activeCategory ? (
                                    <>
                                        {/* <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 'bold', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Select Topic
                                        </div> */}
                                        {Object.keys(QUERY_CATEGORIES).map(category => (
                                            <button 
                                                key={category}
                                                className="predef-item"
                                                onClick={() => setActiveCategory(category)}
                                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                            >
                                                <span>{category}</span>
                                                <ChevronRight size={14} style={{ opacity: 0.5 }} />
                                            </button>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '4px', marginBottom: '4px' }}>
                                            <button 
                                                onClick={() => setActiveCategory(null)}
                                                className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-2"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary-color)' }}>
                                                {activeCategory}
                                            </span>
                                        </div>
                                        <div style={{ overflowY: 'auto', maxHeight: '300px' }}>
                                            {QUERY_CATEGORIES[activeCategory].map((query, idx) => (
                                                <button 
                                                    key={idx}
                                                    className="predef-item" 
                                                    onClick={() => handlePredefinedQuery(query)}
                                                >
                                                    {query}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <button 
                        className="send-btn" 
                        title={isProcessing ? "Processing..." : "Send"}
                        onClick={viewMode === 'paperSearchResults' ? handlePaperSearch : handleSendMessage}
                        disabled={isProcessing || !chatInput.trim()}
                        style={{
                            opacity: (isProcessing || !chatInput.trim()) ? 0.7 : 1,
                            cursor: (isProcessing || !chatInput.trim()) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isProcessing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                </div>
                {/* <div className="hint">
                    {viewMode === 'paperSearchResults' 
                        ? "Enter keywords to search ArXiv/Semantics Scholar." 
                        : "Select a predefined query or type your own."}
                </div> */}
            </div>

        </main>
    );
}

export default CenterPanel;